import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Upload, ShieldCheck, Loader2, FileText, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Reveal from "@/components/site/Reveal";
import { API } from "@/lib/site";

const loanTypes = [
  "Pinjaman Rumah Pertama",
  "Refinance / Pembiayaan Semula",
  "Hartanah Komersial",
  "Pinjaman Peribadi",
  "Lain-lain",
];

export default function ApplicationForm() {
  const [form, setForm] = useState({
    nama: "",
    telefon: "",
    email: "",
    jenis_pinjaman: "",
    jumlah_pinjaman: "",
    mesej: "",
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected].slice(0, 6));
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nama || !form.telefon || !form.email || !form.jenis_pinjaman || !form.jumlah_pinjaman) {
      toast.error("Sila lengkapkan semua ruangan bertanda *");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/applications`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success("Permohonan anda telah dihantar dengan selamat!");
      setForm({ nama: "", telefon: "", email: "", jenis_pinjaman: "", jumlah_pinjaman: "", mesej: "" });
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ralat menghantar permohonan. Sila cuba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="mohon" data-testid="application-section" className="py-24 md:py-32 bg-[#0A2540] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06] bg-grid" aria-hidden="true" />
      <div className="relative mx-auto max-w-6xl px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <Reveal className="lg:col-span-2">
            <span className="text-sm font-semibold uppercase tracking-wider text-[#F59E0B]">
              Mula Sekarang
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-white">
              Semak kelayakan pinjaman anda
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Isi borang ringkas ini. Anda boleh muat naik dokumen sokongan (salinan
              IC, slip gaji, penyata bank) dengan selamat — semua maklumat dirahsiakan.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Lock, t: "Penghantaran disulitkan & sulit" },
                { icon: ShieldCheck, t: "Dokumen hanya untuk permohonan pinjaman anda" },
                { icon: CheckCircle2, t: "Balasan pantas melalui WhatsApp / e-mel" },
              ].map((x) => (
                <div key={x.t} className="flex items-center gap-3">
                  <x.icon className="h-5 w-5 text-[#10B981]" />
                  <span className="text-slate-200 text-sm">{x.t}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-3">
            <div className="rounded-3xl bg-white p-7 md:p-9 shadow-2xl">
              {done ? (
                <div className="text-center py-10" data-testid="application-success">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                    <CheckCircle2 className="h-9 w-9 text-[#10B981]" />
                  </div>
                  <h3 className="mt-5 font-heading text-2xl font-bold text-[#0A2540]">
                    Terima kasih!
                  </h3>
                  <p className="mt-2 text-slate-600">
                    Permohonan anda telah diterima. Saya akan menghubungi anda tidak
                    lama lagi.
                  </p>
                  <Button
                    onClick={() => setDone(false)}
                    className="mt-6 bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full"
                    data-testid="application-new-btn"
                  >
                    Hantar permohonan lain
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="application-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="nama" className="text-[#0A2540] font-medium">Nama Penuh *</Label>
                      <Input id="nama" data-testid="input-nama" value={form.nama}
                        onChange={(e) => update("nama", e.target.value)}
                        placeholder="Nama seperti dalam IC" className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="telefon" className="text-[#0A2540] font-medium">No. Telefon *</Label>
                      <Input id="telefon" data-testid="input-telefon" value={form.telefon}
                        onChange={(e) => update("telefon", e.target.value)}
                        placeholder="01x-xxxxxxx" className="mt-1.5" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="text-[#0A2540] font-medium">E-mel *</Label>
                      <Input id="email" type="email" data-testid="input-email" value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="nama@email.com" className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="jumlah" className="text-[#0A2540] font-medium">Jumlah Pinjaman (RM) *</Label>
                      <Input id="jumlah" data-testid="input-jumlah" value={form.jumlah_pinjaman}
                        onChange={(e) => update("jumlah_pinjaman", e.target.value)}
                        placeholder="cth: 350000" className="mt-1.5" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#0A2540] font-medium">Jenis Pinjaman *</Label>
                    <Select value={form.jenis_pinjaman} onValueChange={(v) => update("jenis_pinjaman", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="select-jenis-pinjaman">
                        <SelectValue placeholder="Pilih jenis pinjaman" />
                      </SelectTrigger>
                      <SelectContent>
                        {loanTypes.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`option-${t}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="mesej" className="text-[#0A2540] font-medium">Mesej (pilihan)</Label>
                    <Textarea id="mesej" data-testid="input-mesej" value={form.mesej}
                      onChange={(e) => update("mesej", e.target.value)}
                      placeholder="Ceritakan sedikit tentang keperluan anda..." className="mt-1.5" rows={3} />
                  </div>

                  <div>
                    <Label className="text-[#0A2540] font-medium">Muat Naik Dokumen (pilihan)</Label>
                    <label
                      htmlFor="file-upload"
                      data-testid="file-upload-label"
                      className="mt-1.5 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-7 cursor-pointer transition-colors hover:border-[#1D4ED8] hover:bg-blue-50/40"
                    >
                      <Upload className="h-6 w-6 text-slate-400" />
                      <span className="text-sm text-slate-600">Klik untuk pilih fail (IC, slip gaji, penyata bank)</span>
                      <span className="text-xs text-slate-400">PDF / JPG / PNG • maks 6MB setiap fail</span>
                      <input id="file-upload" type="file" multiple className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={onFiles} data-testid="input-file" />
                    </label>
                    {files.length > 0 && (
                      <ul className="mt-3 space-y-2" data-testid="file-list">
                        {files.map((f, i) => (
                          <li key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                            <span className="flex items-center gap-2 truncate text-slate-700">
                              <FileText className="h-4 w-4 text-[#1D4ED8]" /> {f.name}
                            </span>
                            <button type="button" onClick={() => removeFile(i)} data-testid={`remove-file-${i}`} className="text-slate-400 hover:text-red-500">
                              <X className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3">
                    <Lock className="h-4 w-4 text-[#10B981]" />
                    <span className="text-xs text-emerald-800 font-medium">
                      Data Dilindungi — maklumat anda selamat &amp; sulit
                    </span>
                  </div>

                  <Button type="submit" disabled={loading} data-testid="submit-application-btn"
                    className="w-full h-12 bg-[#0A2540] hover:bg-[#0d3358] text-white rounded-full text-base transition-transform hover:scale-[1.02]">
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Menghantar...</>
                    ) : (
                      "Hantar Permohonan Selamat"
                    )}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
